import * as React from "react";
import { Icon, SmallIcon, Color } from "@utdanningsdirektoratet/icon";
import { Tooltip } from "@utdanningsdirektoratet/tooltip";
import { isEmpty as isEmptyFunction } from "@utdanningsdirektoratet/utils/string";
import { useId } from "@utdanningsdirektoratet/utils/useId";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import styles from "./Input.module.less";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id?: string;
  label?: React.ReactNode | string;
  disabled?: boolean;
  required?: boolean;
  message?: React.ReactNode | string;
  error?: boolean;
  info?: boolean;
  className?: string;
  icon?: SmallIcon | "none";
  placeholder?: string;
  textAlign?: "left" | "right";
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  erroralways?: boolean;
  visibleIcon?: boolean;
  autoFocus?: boolean;
  selectOnClick?: boolean;
}

const checkIsEmpty = (value?: string | number | readonly string[]): boolean => {
  if (value === "undefined" || value === "NaN") return true;
  return isEmptyFunction(value);
};

const Input = React.forwardRef<HTMLInputElement, InputProps>((props, forwardedRef) => {
  const {
    disabled,
    required,
    message,
    label,
    value,
    type,
    visibleIcon,
    error,
    info,
    autoFocus,
    icon = info && !error ? "circle info" : "warning",
    className,
    onChange,
    onFocus,
    onBlur,
    erroralways,
    textAlign,
    selectOnClick,
    style,
    ...rest
  } = props;

  const id = useId(props.id);
  const [isEmpty, setEmpty] = React.useState(checkIsEmpty(props.defaultValue ?? value));
  const [isFocused, setFocused] = React.useState(false);
  const ref = React.useRef<HTMLInputElement>(null);

  React.useImperativeHandle(forwardedRef, () => ref.current!, [ref]);

  const focus = React.useCallback((): void => {
    if (ref.current) ref.current.focus();
  }, []);

  const select = React.useCallback((): void => {
    if (ref.current) ref.current.select();
  }, []);

  React.useEffect(() => {
    if (autoFocus) focus();
  }, [focus, autoFocus]);

  React.useEffect(() => {
    setEmpty(checkIsEmpty(value ?? props.defaultValue));
  }, [value, props.defaultValue]);

  const onChangeWrapped = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (onChange) {
      onChange(event);
    }
  };

  const onFocusWrapped = (event: React.FocusEvent<HTMLInputElement>): void => {
    if (onFocus) {
      onFocus(event);
    }
    setFocused(true);
  };

  const onBlurWrapped = (event: React.FocusEvent<HTMLInputElement>): void => {
    if (onBlur) {
      onBlur(event);
    }
    setFocused(false);
  };

  const inputClass = cls({
    [styles.input]: true,
    [styles.inputError]: error,
    [styles.inputInfo]: info,
    [styles.inputDisabled]: disabled,
    [styles.inputEmpty]: isEmpty,
    [styles.inputRight]: textAlign === "right",
    [styles.inputWithIcon]: icon && (error || info),
  });

  const iconClass = cls({
    [styles.inputIcon]: true,
    [styles.inputIconVisible]: error || info || visibleIcon,
  });

  const labelClass = cls(
    {
      [styles.inputLabel]: true,
      [styles.inputLabelFocused]: isFocused,
    },
    className
  );

  let iconColor: Color;
  if (info) iconColor = "darkblue";
  if (error) iconColor = "darkred";

  React.useEffect(() => {
    if (!props.defaultValue && (value === undefined || value === null)) {
      if (ref?.current) ref.current.value = (props.defaultValue as string) ?? "";
    }
  }, [value, props.defaultValue]);

  return (
    <label style={style} htmlFor={id} id={`${id}-label`} className={labelClass}>
      {label}
      <Tooltip message={message} disabled={!(error || info)} alwaysShow={erroralways === undefined ? error : erroralways} align="right" alert>
        {({ ref: tooltipRef, ...tooltipProps }) => (
          <div ref={tooltipRef} className={styles.inputTooltipContent}>
            <input
              {...tooltipProps}
              {...rest}
              ref={ref}
              required={required}
              type={type}
              aria-required={required}
              disabled={disabled}
              aria-invalid={error}
              aria-labelledby={label ? `${id}-label` : undefined}
              className={inputClass}
              value={value}
              id={id}
              onChange={onChangeWrapped}
              onFocus={onFocusWrapped}
              onBlur={onBlurWrapped}
              onClick={selectOnClick ? select : undefined}
            />
            {icon ? (
              <Icon
                className={iconClass}
                color={iconColor}
                aria-hidden={error || info || visibleIcon ? "false" : "true"}
                icon={icon}
              />
            ) : null}
          </div>
        )}
      </Tooltip>
    </label>
  );
});

export default Input;
